import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { doRequest } from '@r1-frontend/do-request';
import setTvPacket, { ResponseDto } from '@r1-frontend/api-domru/api-profile/v2/tv-packets/set-tv-packet';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import PopupResult, { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { IOption } from '@r1-frontend/ui-react/components/select/creatableSelect';
import { RubleSymbol } from '@r1-frontend/ui-react/tokens/symbols';
import { pluralize } from '@r1-frontend/shared/helpers/pluralize';
import { dayPluralize, monthPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import TryAndBuyConnectionPopup from '~/src/features/domru-tv/packages/components/TryAndBuyConnectionPopup';
import { TState } from '~/src/store';
import { fetchProfileTvPackages, fetchTvPackages } from '~/src/store/actions/tvPackages';
import { ETryAndBuyDurationTypeId, ETvPackagesActionTypes } from '~/src/store/reducers/tvPackages';
import { selectTrialPackage } from '~/src/store/selectors/tvPackages';

import { ButtonsRow, PopupContent } from '~/src/features/domru-tv/packages/components/PackageConnectPopup/styled';

export interface IConfirmedPhoneParam {
    notifyContactId: string | number,
    notifyPhone?: never,
}

export interface IUnconfirmedPhoneParam {
    notifyContactId?: never,
    notifyPhone: string | number,
}

export interface IPopupResultProps {
    isOpen: boolean,
    title?: string,
    text?: string,
    type: EResultPopupStatus,
}

export const TryAndBuyConnectionForTvServices = () => {
    useEffect(() => {
        dispatch(fetchProfileTvPackages());
    }, []);

    const dispatch = useDispatch();
    const { trialPackage, terminalId } = useSelector((state: TState) => ({
        trialPackage: selectTrialPackage(state),
        terminalId: state.tvPackages.terminalId,
    }), shallowEqual);
    const [isTnBConnecting, setIsTnBConnecting] = useState(false);
    const [tryAndBuyConnectionResult, setTryAndBuyConnectionResult] = useState<IPopupResultProps>({
        isOpen: false,
        type: EResultPopupStatus.DEFAULT,
    });

    const onTryAndBuyConnect = async(phone: IOption) => {
        if (!trialPackage?.tryAndBuy) {
            return;
        }
        setIsTnBConnecting(true);
        const { name, baseCost, tryAndBuy: { duration, durationTypeId, raId } } = trialPackage;
        const tryAndBuyEndDate = durationTypeId === ETryAndBuyDurationTypeId.DAYS
            ? dayjs().add(duration, 'day').format('DD MMMM YYYY')
            : dayjs().add(duration, 'month').format('DD MMMM YYYY');
        const { value, label } = phone;
        const phoneParam: IConfirmedPhoneParam | IUnconfirmedPhoneParam = value === label
            ? { notifyPhone: label }
            : { notifyContactId: value };
        const response = await doRequest.apiProfile<ResponseDto>(
            setTvPacket({
                packetId: trialPackage.id,
                raId,
                trId: terminalId,
                closePacket: 0,
                ...phoneParam,
            }),
        );

        if (response.isSuccess) {
            setTryAndBuyConnectionResult({
                isOpen: true,
                title: `Вы подключили пакет каналов ${name}`,
                text: `Смотрите до ${tryAndBuyEndDate} года бесплатно, затем ${baseCost} ${RubleSymbol}/мес.`,
                type: EResultPopupStatus.SUCCESS,
            });
        } else {
            setTryAndBuyConnectionResult({
                isOpen: true,
                title: response.error.message ?? 'Что-то пошло не так. Попробуйте ещё раз.',
                text: '',
                type: EResultPopupStatus.ERROR,
            });
        }
        setIsTnBConnecting(false);
        closeTnBPopup();
    };

    const tryAndBuyLabel = useMemo(() => {
        if (!trialPackage) {
            return;
        }
        const { state, tryAndBuy } = trialPackage;
        if (state?.actDateTo) {
            return `промо-цена до ${dayjs(new Date(state.actDateTo * 1000)).format('DD.MM')}`;
        }
        if (tryAndBuy) {
            const { duration, durationTypeId } = tryAndBuy;
            const periodText = durationTypeId === ETryAndBuyDurationTypeId.DAYS
                ? `${duration} ${pluralize(duration, dayPluralize)}`
                : `${duration} ${pluralize(duration, monthPluralize)}`;

            return `${periodText} бесплатно`;
        }
    }, [trialPackage]);

    const closeTnBPopup = () => {
        dispatch({
            type: ETvPackagesActionTypes.SET_TRIAL_PACKAGE,
            payload: {
                trialPackage: null,
            },
        });
    };

    const onCloseResultHandler = () => {
        if (tryAndBuyConnectionResult?.type === EResultPopupStatus.SUCCESS) {
            dispatch(fetchTvPackages());
        }
        setTryAndBuyConnectionResult({
            isOpen: false,
            type: EResultPopupStatus.DEFAULT,
        });
    };

    const { isOpen, title = '', text = '', type } = tryAndBuyConnectionResult;
    return (
        <>
            <TryAndBuyConnectionPopup
                isOpen={!!trialPackage}
                isConnectionInProgress={isTnBConnecting}
                title={`Подключить ${trialPackage?.name}`}
                labelText={tryAndBuyLabel ?? ''}
                onConnect={onTryAndBuyConnect}
                onClose={closeTnBPopup}
            />
            <PopupResult
                isOpen={!!isOpen}
                title={title}
                text={text}
                type={type}
                onClose={onCloseResultHandler}
            >
                <PopupContent>
                    <ButtonsRow>
                        <Button
                            bType={'action'}
                            size={'large'}
                            onClick={onCloseResultHandler}
                        >
                            Закрыть
                        </Button>
                    </ButtonsRow>
                </PopupContent>
            </PopupResult>
        </>
    );
};
